import React, { FC } from 'react';
import { NoBmisAsideDetails } from '@packages/ui/bmis/bmis-aside-details';
import { createUseStyles } from 'react-jss';
import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';
import { useDmsState } from '@packages/contexts/dms';
import { DMSAsideDetails } from './dms-aside-details';
import { useAuthState } from '@packages/contexts/auth';
import { useMemo } from 'react';

const useDMSAsideContentStyles = createUseStyles({
    asideHeader: {
        position: 'relative',
        borderBottom: `1px solid ${colors.grayThree}`,
        padding: '16px 24px',
        marginBottom: '16px',
        '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 24,
            height: '3px',
            width: '60px',
            backgroundColor: colors.blueOne,
        },
    },
});

export const DMSAsideContent: FC = () => {
    const { t } = useTranslation();
    const classes = useDMSAsideContentStyles();
    const { selectedRepairOrder } = useDmsState();
    const { account } = useAuthState();

    const allowedRoles = ['SOA_SUPER_USER', 'QA_ADMIN', 'NASI_SIA_ADMIN', 'LAG_SUPER_ADMIN', 'DSQM', 'RSQM'];

    const hasAllowedRole = useMemo(() => {
        return (
            Array.isArray(account?.profiles) &&
            account.profiles.some((profile) => allowedRoles.includes(profile.roleId))
        );
    }, [account]);

    const hasMatchingLocation = useMemo(() => {
        if (!selectedRepairOrder?.retailerNumber || !Array.isArray(account?.locations)) return false;
        return account.locations.some(
            (location) =>
                Array.isArray(location.elements) &&
                location.elements.some(
                    (el) =>
                        el.accountLocationElementType === 'ID' &&
                        typeof el.value === 'string' &&
                        el.value.replace('#', '') === selectedRepairOrder.retailerNumber
                )
        );
    }, [account, selectedRepairOrder]);

    const showAsideDetails =
        (hasAllowedRole && selectedRepairOrder?.retailerNumber) ||
        (!hasAllowedRole && selectedRepairOrder?.retailerNumber && hasMatchingLocation) ||
        account?.dmsCapabilities?.dmsRORetailerDetailsView;

    return (
        <>
            <div className={classes.asideHeader}>
                <Typography variant="h5" color="blueOne">
                    {t('Details')}
                </Typography>
            </div>
            {showAsideDetails ? (
                <DMSAsideDetails
                    retailerNumber={selectedRepairOrder?.retailerNumber ?? ''}
                    retailerName={selectedRepairOrder?.retailerName ?? ''}
                />
            ) : (
                <NoBmisAsideDetails />
            )}
        </>
    );
};
